import {
  AdvancedSettings,
  AmountType,
  AnalyticsPageName,
  BacktestingStatus,
  DCAStrategyData,
  DefaultOrder,
  Direction,
  HelperTooltipWithTranslations,
  LanguageNameSpaces,
  LongShort,
  NumberOption,
  OrderBaseline,
  OverrideFieldType,
  StopType,
  StrategyData,
  StrategyStyleType,
  UnifiedStrategyData,
} from "@shared/types";
import {
  BacktestingPnlStatus,
  BaseBotFormValues,
  BotFormSetters,
  BotFormValues,
  EntryGraceBaseline,
  LeverageType,
  OverlapData,
  ParsedDcaStrategyData,
  RestoreChangesType,
  Steps,
  StrategyBaseline,
  StrategyTabs,
  TargetsTabs,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import { OrderTypes } from "@shared/features/trades/common/types";
import { invert } from "lodash";
import { STOP_TYPE_TO_DISPLAY } from "@shared/common/constants";
import { Color } from "@shared/common/Themes/types";
import { TranslationKeys } from "@shared/common/i18n/types";

export const MAX_PRICE = 1000000;
export const MAX_NUM_GRIDS = 75;
export const MIN_NUM_GRIDS = 3;
export const MAX_SECONDS_COOLDOWN = 1000000;
export const GENERAL_MAX_VALUE = 100000;
export const MAX_SIMULTANEOUS_TRADES_PER_SYMBOL = 30;
export const NO_LIMIT_MAX_VALUE = 999999999999;
export const MAX_DEFAULT_TAKE_PROFIT_PERCENT = 100;
export const MAX_BOT_NAME_LENGTH = 50;
export const MAX_BACKTEST_NAME_LENGTH = 50;
export const MAX_BACKEND_BOT_NAME_LENGTH = 64;
export const MAX_FETCH_ALL_ACCOUNT_AMOUNTS = 5;

export const LONG_SHORT_OPTIONS = [
  { name: Direction.LONG, value: LongShort.LONG },
  { name: Direction.SHORT, value: LongShort.SHORT },
];

export const STOP_TYPE_OPTIONS = [
  { name: STOP_TYPE_TO_DISPLAY[StopType.LIMIT], value: StopType.LIMIT },
  { name: STOP_TYPE_TO_DISPLAY[StopType.MARKET], value: StopType.MARKET },
];

export const BOT_FORM_TABS_ORDER = [
  Steps.GENERAL,
  Steps.ENTRIES,
  Steps.TAKE_PROFITS,
  Steps.STOP,
  Steps.ADVANCED,
];

export const ANALYTICS_FIELD_NAMES: Record<
  OrderTypes,
  TranslationKeys<LanguageNameSpaces.BOTS>
> = {
  [OrderTypes.ENTRY]: "ordersSummary.analyticsFieldNames.entry",
  [OrderTypes.TAKE_PROFIT]: "ordersSummary.analyticsFieldNames.take_profit",
  [OrderTypes.STOP]: "ordersSummary.analyticsFieldNames.stop",
};

export const DCA_BOT_HELP_DESCRIPTIONS: Record<
  OrderTypes,
  TranslationKeys<LanguageNameSpaces.BOTS>
> = {
  [OrderTypes.ENTRY]: "ordersSummary.help.dca.entry",
  [OrderTypes.TAKE_PROFIT]: "ordersSummary.help.dca.take_profit",
  [OrderTypes.STOP]: "ordersSummary.help.dca.stop",
};

export const TRADING_VIEW_BOT_HELP_DESCRIPTIONS: Record<
  OrderTypes,
  TranslationKeys<LanguageNameSpaces.BOTS>
> = {
  [OrderTypes.ENTRY]: "ordersSummary.help.tradingView.entry",
  [OrderTypes.TAKE_PROFIT]: "ordersSummary.help.tradingView.take_profit",
  [OrderTypes.STOP]: "ordersSummary.help.tradingView.stop",
};

export const channelValue = Symbol.for("channel_value");
export const multiValue = Symbol.for("multi");
export const multiToggleValue = Symbol.for("multi_toggle");
export const empty = "empty";
export const off = "off";

export const DEFAULT_TAKE_PROFIT = {
  percent: "1",
  baseline: OrderBaseline.AVERAGE_ENTRIES,
};
export const DEFAULT_FIRST_ENTRY_TAKE_PROFIT = {
  percent: "1",
  baseline: OrderBaseline.FIRST_ENTRY,
};
export const DEFAULT_SIGNAL_BOT_TAKE_PROFIT: DefaultOrder<string> = {
  percent: channelValue,
  baseline: OrderBaseline.AVERAGE_ENTRIES,
};
export const DEFAULT_STOP = {
  percent: "5",
  baseline: OrderBaseline.AVERAGE_ENTRIES,
};
export const DEFAULT_GRID_BOT_STOP = {
  percent: "5",
  baseline: OrderBaseline.LAST_ENTRY,
};
export const DEFAULT_STRATEGY_TARGETS = [25, 25, 25, 25];
export const DEFAULT_FULL_STRATEGY_TARGETS = [
  10, 10, 10, 10, 10, 10, 10, 10, 10, 10,
];

export const RESET_TAB_BUTTON_HEIGHT = 28;

export const FIELD_TO_PARENT_FIELD: { [key in ToggleField]?: ToggleField } = {
  [ToggleField.FIRST_ENTRY]: ToggleField.ENTRIES,
  [ToggleField.TRAILING_ENTRY]: ToggleField.ENTRIES,
  [ToggleField.ENTRY_GRACE_PERCENT]: ToggleField.ENTRIES,
  [ToggleField.TAKE_PROFIT_STRATEGY]: ToggleField.TAKE_PROFITS,
  [ToggleField.TRAILING_TAKE_PROFIT]: ToggleField.TAKE_PROFITS,
  [ToggleField.TAKE_PROFIT_GRACE_PERCENT]: ToggleField.TAKE_PROFITS,
  [ToggleField.STOP_TIMEOUT]: ToggleField.STOP,
  [ToggleField.TRAILING_STOP]: ToggleField.STOP,
};

export const FORM_KEY_TO_TOGGLE_FIELD: {
  [key in keyof BaseBotFormValues]?: ToggleField;
} = {
  entryStrategy: ToggleField.ENTRIES,
  trailingEntry: ToggleField.TRAILING_ENTRY,
  entryGracePercent: ToggleField.ENTRY_GRACE_PERCENT,
  defaultTakeProfit: ToggleField.TAKE_PROFITS,
  takeProfitStrategy: ToggleField.TAKE_PROFIT_STRATEGY,
  trailingTakeProfit: ToggleField.TRAILING_TAKE_PROFIT,
  defaultStop: ToggleField.STOP,
  stopTimeout: ToggleField.STOP_TIMEOUT,
  trailingStopData: ToggleField.TRAILING_STOP,
  cancelOnProfitBeforeEntry: ToggleField.CLOSE_ON_TP_BEFORE_ENTRY,
  skipWithoutStopLoss: ToggleField.SKIP_WITHOUT_STOP_LOSS,
  takeProfitGracePercent: ToggleField.TAKE_PROFIT_GRACE_PERCENT,
  operationHoursEvents: ToggleField.OPERATION_HOURS,
  amountType: ToggleField.AMOUNT_PER_TRADE,
};

export const FORM_KEY_TO_OVERRIDE_FIELD: {
  [key in keyof BaseBotFormValues]?: OverrideFieldType;
} = {
  amountValue: OverrideFieldType.AMOUNT_PER_TRADE,
  cancelOnProfitBeforeEntry: OverrideFieldType.CANCEL_ON_PROFIT_BEFORE_ENTRY,
  defaultStop: OverrideFieldType.DEFAULT_STOP,
  defaultTakeProfit: OverrideFieldType.DEFAULT_TAKE_PROFIT,
  entryStrategy: OverrideFieldType.ENTRY_STRATEGY,
  takeProfitStrategy: OverrideFieldType.TAKE_PROFIT_STRATEGY,
  trailingEntry: OverrideFieldType.TRAILING_ENTRY,
  trailingStopData: OverrideFieldType.TRAILING_STOP,
  trailingTakeProfit: OverrideFieldType.TRAILING_TAKE_PROFIT,
  multiSymbolMarginData: OverrideFieldType.LEVERAGE,
};

export const TOGGLE_FIELD_TO_FORM_KEY = invert(FORM_KEY_TO_TOGGLE_FIELD) as {
  [key in ToggleField]?: keyof BaseBotFormValues;
};

export const BOT_FORM_KEY_TO_SETTER_NAME: Record<
  keyof BotFormValues,
  keyof BotFormSetters
> = {
  name: "setName",
  useSymbolFromAlert: "setUseSymbolFromAlert",
  subGroup: "setSubGroup",
  account: "setAccount",
  accounts: "setAccounts",
  symbols: "setSymbols",
  exchanges: "setExchanges",
  includeNewSymbols: "setIncludeNewSymbols",
  subGroupBotConfiguration: "setSubGroupBotConfiguration",
  trailingStopData: "setTrailingStopData",
  entryStrategy: "setEntryStrategy",
  takeProfitStrategy: "setTakeProfitStrategy",
  defaultTakeProfit: "setDefaultTakeProfit",
  defaultStop: "setDefaultStop",
  amountType: "setAmountType",
  amountValue: "setAmountValue",
  amountPercentageBaseType: "setAmountPercentageBaseType",
  amountCustomBaseValue: "setAmountCustomBaseValue",
  longShort: "setLongShort",
  marginType: "setMarginType",
  multiSymbolMarginData: "setMultiSymbolMarginData",
  multiplier: "setMultiplier",
  trailingEntry: "setTrailingEntry",
  trailingTakeProfit: "setTrailingTakeProfit",
  trailingTakeProfitLastTargetOnly: "setTrailingTakeProfitLastTargetOnly",
  stopTimeout: "setStopTimeout",
  advancedSettings: "setAdvancedSettings",
  stopType: "setStopType",
  stopLimitPriceReduction: "setStopLimitPriceReduction",
  cancelOnProfitBeforeEntry: "setCancelOnProfitBeforeEntry",
  entryGracePercent: "setEntryGracePercent",
  entryGraceBaseline: "setEntryGraceBaseline",
  numberOfContinuousStopsTillBotStop: "setNumberOfContinuousStopsTillBotStop",
  gridsData: "setGridsData",
  hedgingMode: "setHedgingMode",
  hedgingGridsData: "setHedgingGridsData",
  initialized: "setInitialized",
  checkedFields: "setCheckedFields",
  overrideFields: "setOverrideFields",
  isLeverageExchanges: "setIsLeverageExchanges",
  skipWithoutStopLoss: "setSkipWithoutStopLoss",
  alternativeUsdPairs: "setAlternativeUsdPairs",
  followFuturesAsSpot: "setFollowFuturesAsSpot",
  takeProfitGracePercent: "setTakeProfitGracePercent",
  entryGraceTargetExchange: "setEntryGraceTargetExchange",
  takeProfitGraceTargetExchange: "setTakeProfitGraceTargetExchange",
  operationHoursEvents: "setOperationHoursEvents",
  keywordToSymbolMapping: "setKeywordToSymbolMapping",
  longHedgingRatio: "setLongHedgingRatio",
};

export const MULTI_VALUE_TOGGLE_BOT_KEYS: Set<
  keyof BaseBotFormValues | keyof DCAStrategyData
> = new Set([
  "trailingStopData",
  "entryStrategy",
  "takeProfitStrategy",
  "hedgingMode",
  "defaultTakeProfit",
  "defaultStop",
  "trailingEntry",
  "trailingTakeProfit",
  "stopTimeout",
  "first_order_percentage",
  "amountType",
  "amountValue",
]);

export const STOP_TIMEOUT_TITLE = "labels.stop_timeout";
export const STOP_BASELINE_TITLE = "titles.stop_loss_baseline";
export const STOP_PERCENT_TITLE = "titles.stop_loss_distance_percent";
export const TAKE_PROFIT_BASELINE_TITLE = "titles.take_profit_baseline";
export const TAKE_PROFIT_PERCENT_TITLE = "titles.take_profit_distance_percent";

export const DCA_STRATEGY_KEY_TO_TITLE: {
  [key in keyof ParsedDcaStrategyData]: TranslationKeys<LanguageNameSpaces.BOTS>;
} = {
  baseline: "strategy.baseline",
  numOrders: "strategy.num_orders",
  numActiveOrders: "strategy.num_active_orders",
  firstOrderPercent: "strategy.first_entry_order_amount_percent",
  amountScale: "strategy.amount_scale",
  priceDifference: "strategy.price_difference",
  priceScale: "strategy.price_scale",
};
export const MAX_PRICE_DIFFERENCE_TEXT = "strategy.max_orders_price_difference";
export const GRID_MAX_PRICE_DIFFERENCE_TEXT =
  "strategy.max_grids_price_difference";

export const ALL_OVERRIDES_TOGGLE_FIELDS = new Set([
  ToggleField.TRAILING_TAKE_PROFIT,
  ToggleField.TRAILING_ENTRY,
  ToggleField.STOP,
  ToggleField.TRAILING_STOP,
  ToggleField.CLOSE_ON_TP_BEFORE_ENTRY,
]);
export const WITHOUT_OFF_TOGGLE_FIELDS = new Set([
  ToggleField.ENTRIES,
  ToggleField.ENTRY_RATIOS,
  ToggleField.ENTRY_PRICE_STRATEGY,
  ToggleField.ENTRY_NUM_ORDERS,
  ToggleField.ENTRY_NUM_ACTIVE_ORDERS,
  ToggleField.TAKE_PROFITS,
  ToggleField.TAKE_PROFIT_STRATEGY,
  ToggleField.TAKE_PROFIT_RATIOS,
  ToggleField.TAKE_PROFIT_PRICE_STRATEGY,
  ToggleField.TAKE_PROFIT_NUM_ORDERS,
  ToggleField.TAKE_PROFIT_NUM_ACTIVE_ORDERS,
  ToggleField.MARGIN,
]);
export const WITHOUT_CHANNEL_TOGGLE_FIELDS = new Set([
  ToggleField.ENTRY_GRACE_PERCENT,
  ToggleField.STOP_TIMEOUT,
  ToggleField.TAKE_PROFIT_DYNAMIC,
  ToggleField.SKIP_WITHOUT_STOP_LOSS,
  ToggleField.ALTERNATIVE_USD_PAIRS,
  ToggleField.TAKE_PROFIT_GRACE_PERCENT,
  ToggleField.OPERATION_HOURS,
]);

export const TOGGLE_FIELD_TO_OVERRIDE_KEY: {
  [key in ToggleField]?: OverrideFieldType;
} = {
  [ToggleField.TRAILING_ENTRY]: OverrideFieldType.TRAILING_ENTRY,
  [ToggleField.TRAILING_TAKE_PROFIT]: OverrideFieldType.TRAILING_TAKE_PROFIT,
  [ToggleField.TRAILING_STOP]: OverrideFieldType.TRAILING_STOP,
  [ToggleField.TAKE_PROFITS]: OverrideFieldType.DEFAULT_TAKE_PROFIT,
  [ToggleField.STOP]: OverrideFieldType.DEFAULT_STOP,
  [ToggleField.ENTRIES]: OverrideFieldType.ENTRY_STRATEGY,
  [ToggleField.ENTRY_RATIOS]: OverrideFieldType.ENTRY_STRATEGY,
  [ToggleField.TAKE_PROFIT_RATIOS]: OverrideFieldType.TAKE_PROFIT_STRATEGY,
  [ToggleField.TAKE_PROFIT_STRATEGY]: OverrideFieldType.TAKE_PROFIT_STRATEGY,
  [ToggleField.MARGIN]: OverrideFieldType.LEVERAGE,
  [ToggleField.CLOSE_ON_TP_BEFORE_ENTRY]:
    OverrideFieldType.CANCEL_ON_PROFIT_BEFORE_ENTRY,
};

export const ADVANCED_KEY_TO_INPUT_DISPLAY_NAME: Record<
  keyof AdvancedSettings,
  TranslationKeys<LanguageNameSpaces.BOTS>
> = {
  number_of_trades_till_bot_stop: "advanced.number_of_trades_till_bot_stop",
  cooldown_between_opening_trades: "advanced.cooldown_between_opening_trades",
  cooldown_after_trade_close: "advanced.cooldown_after_trade_close",
  number_of_simultaneous_trades: "advanced.number_of_simultaneous_trades",
  cooldown_between_opening_trades_per_symbol:
    "advanced.cooldown_between_opening_trades_per_symbol",
  min_symbol_price: "advanced.min_symbol_price_usd",
  min_symbol_volume: "advanced.min_symbol_24h_volume_usd",
  concurrent_max_usd_invested: "advanced.max_concurrent_amount_usd",
  number_of_simultaneous_trades_per_symbol:
    "advanced.number_of_simultaneous_trades_per_symbol",
  auto_cancel_timeout_seconds: "advanced.auto_cancel_trade_timeout",
  time_stop_seconds: "advanced.time_stop_seconds",
};

export const CANCEL_ON_PROFIT_BEFORE_ENTRY_TITLE =
  "labels.close_trade_on_tp_before_entry";
export const DYNAMIC_TAKE_PROFITS_TITLE = "labels.moving_take_profits";
export const PRICE_ADJUSTMENT_BASELINE_TITLE =
  "titles.price_adjustment_baseline";
export const STOP_LIMIT_PRICE_REDUCTION_TITLE =
  "labels.stop_limit_price_reduction";
export const SKIP_SIGNALS_WITHOUT_STOP_TITLE =
  "labels.skip_signals_without_stop_loss";
export const ALTERNATIVE_USD_PAIRS_TITLE = "labels.alternative_usd_pairs";
export const KEYWORD_TO_SYMBOL_TITLE = "labels.keyword_to_symbol";
export const STOP_TYPE_TITLE = "labels.stop_type";
export const TAKE_PROFIT_GRACE_TITLE = "labels.take_profit_grace";
export const GRACE_TARGET_EXCHANGE_FEATURE_FLAG = "grace_target_exchange";
export const OPERATION_HOURS_TITLE = "labels.operation_hours";

export const RESTORE_CHANGES_TYPE_TO_DISPLAY: Record<
  RestoreChangesType,
  TranslationKeys<LanguageNameSpaces.BOTS>
> = {
  [RestoreChangesType.ORIGINAL_BOT]: "buttons.restore_previous_values",
  [RestoreChangesType.TEMPLATE]: "buttons.restore_template_values",
};

export const STRATEGY_BASELINE_TO_DISPLAY = {
  [StrategyBaseline.PREVIOUS_ORDER]: "Previous Grid",
  [StrategyBaseline.FIRST_ORDER]: "First Grid",
};

export const ORDER_BASELINE_TO_DISPLAY: { [key in OrderBaseline]?: string } = {
  [OrderBaseline.FIRST_ENTRY]: "First Entry",
  [OrderBaseline.AVERAGE_ENTRIES]: "Average Entries",
  [OrderBaseline.LAST_ENTRY]: "Last Entry",
};

export const DEFAULT_GRID_BOT_STRATEGY: DCAStrategyData = {
  first_order_percentage: undefined,
  strategy_style: StrategyStyleType.DCA,
  amount_scale: 1,
  price_scale: 1,
  price_difference: 0.6,
  number_of_orders: 50,
  number_of_active_orders: 3,
  baseline: StrategyBaseline.FIRST_ORDER,
};

export const DEFAULT_GRID_PRICE_RANGE_RATIO = 0.3;
export const DEFAULT_AMOUNT_PERCENT = 10;
export const DEFAULT_STOP_LIMIT_PRICE_REDUCTION = 2;
export const MIN_CONSECUTIVE_GRIDS_DISTANCE = 0.2;
export const INVALID_PRICES_ERROR_MESSAGE =
  "Some of the prices below are invalid";
export const INVALID_GRIDS_DISTANCE_ERROR_MESSAGE = `The minimum distance between consecutive grids should be at least ${MIN_CONSECUTIVE_GRIDS_DISTANCE}%.`;

export const TV_BOT_ORDERS_SORTER_HEIGHT = 56;

export const OVERLAP_KEY_TO_DISPLAY: Record<
  keyof OverlapData,
  TranslationKeys<LanguageNameSpaces.BOTS>
> = {
  symbols: "labels.symbols",
  stopType: "labels.stop_type",
  longShort: "labels.direction",
  marginType: "labels.margin_type",
  multiplier: "labels.multiplier",
};

export const STRATEGY_TYPE_MAPPING = {
  [StrategyStyleType.CUSTOM]: StrategyTabs.CUSTOM,
  [StrategyStyleType.DCA]: StrategyTabs.DCA,
  [StrategyStyleType.BUILT_IN]: StrategyTabs.BUILT_IN,
};

export const ADVANCED_BOT_FORM_KEYS: (keyof BaseBotFormValues)[] = [
  "trailingEntry",
  "entryGracePercent",
  "trailingTakeProfit",
  "trailingTakeProfitLastTargetOnly",
  "stopTimeout",
  "cancelOnProfitBeforeEntry",
  "multiSymbolMarginData",
];

export const ADVANCED_SETTINGS_KEYS: (keyof AdvancedSettings)[] = [
  "number_of_trades_till_bot_stop",
  "cooldown_between_opening_trades",
  "cooldown_after_trade_close",
  "number_of_simultaneous_trades",
  "number_of_simultaneous_trades_per_symbol",
  "cooldown_between_opening_trades_per_symbol",
  "min_symbol_price",
  "min_symbol_volume",
  "concurrent_max_usd_invested",
  "auto_cancel_timeout_seconds",
  "time_stop_seconds",
];

export const STRATEGY_KEYS: (keyof ParsedDcaStrategyData | "targets")[] = [
  "numOrders",
  "numActiveOrders",
  "amountScale",
  "priceDifference",
  "priceScale",
  "firstOrderPercent",
  "baseline",
  "targets",
];

export const DEFAULT_ADVANCED_SETTINGS_VALUE = {
  number_of_trades_till_bot_stop: undefined,
  cooldown_between_opening_trades: undefined,
  cooldown_after_trade_close: undefined,
  number_of_simultaneous_trades: undefined,
  cooldown_between_opening_trades_per_symbol: undefined,
  min_symbol_price: undefined,
  min_symbol_volume: undefined,
  concurrent_max_usd_invested: undefined,
  number_of_simultaneous_trades_per_symbol: undefined,
  auto_cancel_timeout_seconds: undefined,
  time_stop_seconds: undefined,
};

export const DEFAULT_NUMBER_OF_SIMULTANEOUS_TRADES_PER_SYMBOL = "5";

export const DEFAULT_SIGNALS_BOT_ADVANCED_SETTINGS_VALUE = {
  ...DEFAULT_ADVANCED_SETTINGS_VALUE,
  number_of_simultaneous_trades_per_symbol:
    DEFAULT_NUMBER_OF_SIMULTANEOUS_TRADES_PER_SYMBOL,
};

export const LEVERAGE_TYPE_OPTIONS: NumberOption[] = [
  { value: LeverageType.EXACT, name: "Exactly" },
  { value: LeverageType.UP_TO, name: "Up to" },
];

export const DEFAULT_SIGNALS_BOT_STRATEGY: StrategyData = {
  strategy_style: channelValue,
  baseline: channelValue,
  builtin_strategy_type: channelValue,
  amount_scale: channelValue,
  price_scale: channelValue,
  price_difference: channelValue,
  number_of_orders: channelValue,
  number_of_active_orders: channelValue,
};

export const DEFAULT_SIGNALS_CONFIGURATION_STRATEGY: StrategyData = {
  strategy_style: channelValue,
  baseline: undefined,
  builtin_strategy_type: undefined,
  amount_scale: undefined,
  price_scale: undefined,
  price_difference: undefined,
  number_of_orders: undefined,
  number_of_active_orders: undefined,
};

export const ENTRY_STRATEGY_TOGGLE_FIELDS = new Set([
  ToggleField.ENTRY_RATIOS,
  ToggleField.ENTRY_PRICE_STRATEGY,
  ToggleField.ENTRY_NUM_ORDERS,
  ToggleField.ENTRY_NUM_ACTIVE_ORDERS,
]);

export const TOGGLE_FIELD_TO_STRATEGY_FIELDS: {
  [key in ToggleField]?: (keyof UnifiedStrategyData)[];
} = {
  [ToggleField.ENTRY_RATIOS]: [
    "strategy_style",
    "first_order_percentage",
    "amount_scale",
    "builtin_strategy_type",
    "targets",
  ],
  [ToggleField.TAKE_PROFIT_RATIOS]: [
    "strategy_style",
    "first_order_percentage",
    "amount_scale",
    "builtin_strategy_type",
    "targets",
  ],
  [ToggleField.ENTRY_PRICE_STRATEGY]: ["price_scale", "price_difference"],
  [ToggleField.TAKE_PROFIT_PRICE_STRATEGY]: ["price_scale", "price_difference"],
  [ToggleField.ENTRY_NUM_ORDERS]: ["number_of_orders"],
  [ToggleField.TAKE_PROFIT_NUM_ORDERS]: ["number_of_orders"],
  [ToggleField.ENTRY_NUM_ACTIVE_ORDERS]: ["number_of_active_orders"],
  [ToggleField.TAKE_PROFIT_NUM_ACTIVE_ORDERS]: ["number_of_active_orders"],
  [ToggleField.FIRST_ENTRY]: ["first_order_percentage"],
};

export const LEVERAGED_TRAILING_MESSAGE = "messages.info.leveraged_trailing";
export const BOT_MARGIN_DATA_MODIFIED_MESSAGE =
  "The multiplier for some of the symbols is different than the original bot.";
export const BOT_MAX_LEVERAGE_WARNING_MESSAGE =
  "The multiplier for some of the symbols was adjusted to the maximum that is allowed on the exchange.";
export const SIGNALS_BOT_OVERRIDE_TEXT = "labels.use_not_defined_by_group";

export const DEFAULT_OVERRIDE_FIELDS = new Set([
  OverrideFieldType.AMOUNT_PER_TRADE,
]);

export const BACKTESTING_STATUS_TO_COLOR = {
  [BacktestingStatus.IN_PROGRESS]: "orange",
  [BacktestingStatus.COMPLETED]: "green",
  [BacktestingStatus.FAILED]: "red",
};

export const BACKTESTING_STATUS_TO_DISPLAY_NAME: {
  [key in BacktestingStatus]: TranslationKeys<LanguageNameSpaces.COMMON>;
} = {
  [BacktestingStatus.IN_PROGRESS]: "filters.status.backtesting.in_progress",
  [BacktestingStatus.COMPLETED]: "filters.status.backtesting.completed",
  [BacktestingStatus.FAILED]: "filters.status.backtesting.failed",
};

export const BACKTESTING_PNL_STATUS_TO_COLOR: Record<
  BacktestingPnlStatus,
  Color
> = {
  [BacktestingPnlStatus.WINNING]: "green",
  [BacktestingPnlStatus.LOSING]: "red",
  [BacktestingPnlStatus.NEUTRAL]: "yellow",
};

export const TARGETS_STATISTIC_TABS_TO_DISPLAY: Record<
  TargetsTabs,
  TranslationKeys<LanguageNameSpaces.BOTS>
> = {
  [TargetsTabs.ENTRIES]: "backtesting.statistics.entries",
  [TargetsTabs.TAKE_PROFITS]: "backtesting.statistics.take_profits",
};

export const TARGETS_STATISTIC_TABS_TO_STROKE_COLOR = {
  [TargetsTabs.ENTRIES]: { "0%": "#B8F0BB", "100%": "#0AC032" },
  [TargetsTabs.TAKE_PROFITS]: { "0%": "#FFB8B4", "100%": "#DB3328" },
};

export const BACKTESTING_IN_PROGRESS_MESSAGE =
  "backtesting.messages.in_progress";
export const FAILED_BACKTESTING_EXTRA_DETAILS_MESSAGE =
  "backtesting.messages.failed_extra_details";

export const BACKTESTING_DISCLAIMER = "backtesting.messages.disclaimer";
export const BACKTESTING_DISCLAIMER_ARTICLE = "9572268";
export const BACKTESTING_VALUES_MODIFIED_MESSAGE =
  "backtesting.messages.values_modified";

export const BACKTESTING_MAX_DRAWDOWN_HELPER_PROPS: HelperTooltipWithTranslations<LanguageNameSpaces.BOTS> =
  {
    description: "backtesting.helper_tooltips.max_drawdown",
    analyticsPageName: AnalyticsPageName.BACKTESTING,
    analyticsFieldName: "Max Drawdown",
    articleId: "9575281",
    title: "backtesting.statistics.max_drawdown",
  };

export const AVG_TRADE_DURATION_DRAWDOWN_HELPER_PROPS: HelperTooltipWithTranslations<LanguageNameSpaces.BOTS> =
  {
    description: "backtesting.helper_tooltips.avg_trade_duration",
    analyticsPageName: AnalyticsPageName.BACKTESTING,
    analyticsFieldName: "Avg Trade Duration",
    articleId: "9575281",
    title: "backtesting.statistics.avg_trade_duration",
  };

export const MAX_CHANNEL_CONFIGURATION_PERCENT = 20;

export const ENTRY_GRACE_BASELINE_TO_DISPLAY_TEXT: Record<
  EntryGraceBaseline,
  TranslationKeys<LanguageNameSpaces.BOTS>
> = {
  [EntryGraceBaseline.PRICE_REACHED]:
    "entry_grace_baseline.entry_price_reached",
  [EntryGraceBaseline.IMMEDIATE]: "entry_grace_baseline.immediately",
};

export const MARGIN_GLOBAL_SETTINGS_KEY = "FALLBACK_SYMBOL";
export const SHOW_MODIFIED_SYMBOLS_TITLE =
  "backtesting.labels.show_custom_symbols";

export const PERCENTAGE_AMOUNT_OPTIONS = [
  { value: AmountType.PERCENTAGE, name: "backtesting.labels.percentage" },
  {
    value: AmountType.RISK_PERCENTAGE,
    name: "backtesting.labels.risk_percentage",
  },
] as const;
